import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CalendarDays, Pencil, Plus, Trash2, X } from 'lucide-react';
import Layout from '../components/layout/Layout.tsx';
import {
  fetchInfoExtras,
  createInfoExtra,
  updateInfoExtra,
  deleteInfoExtra,
} from '../services/api.ts';

interface InfoExtra {
  id: number;
  valor: string;
  date_begin?: string | null;
  date_end?: string | null;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
];

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-4 focus:ring-teal-600/10';

const DATE_TOOLTIP_TEXT =
  'Opcional: você pode cadastrar somente data de início ou somente data de fim para a regra.';

const DATETIME_LOCAL_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const formatDateToDatetimeLocal = (date: Date) => {
  const pad = (num: number) => String(num).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const parseDatetimeLocal = (value: string): Date | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;
  if (!DATETIME_LOCAL_REGEX.test(trimmedValue)) return null;
  const parsed = new Date(trimmedValue);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const getEndMinValue = (beginValue: string) => {
  const beginDate = parseDatetimeLocal(beginValue);
  if (!beginDate) return undefined;
  const minEnd = new Date(beginDate.getTime() + 60 * 1000);
  return formatDateToDatetimeLocal(minEnd);
};

const validateDateRange = (beginValue: string, endValue: string) => {
  const beginDate = parseDatetimeLocal(beginValue);
  const endDate = parseDatetimeLocal(endValue);

  if (beginValue.trim() && !beginDate) {
    return 'Data de início inválida. Use o formato AAAA-MM-DDTHH:mm.';
  }

  if (endValue.trim() && !endDate) {
    return 'Data de fim inválida. Use o formato AAAA-MM-DDTHH:mm.';
  }

  if (beginDate && endDate && endDate.getTime() <= beginDate.getTime()) {
    return 'A data de fim deve ser maior que a data de início.';
  }

  if (!beginDate && endDate && endDate.getTime() <= Date.now()) {
    return 'Ao informar apenas a data de fim, ela deve ser maior que a data e hora atual.';
  }

  return null;
};

const toDatetimeLocalInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateToDatetimeLocal(date);
};

const formatDisplayDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getRangeLabel = (item: InfoExtra) => {
  const parts: string[] = [];
  const inicio = formatDisplayDate(item.date_begin);
  const termino = formatDisplayDate(item.date_end);
  if (inicio) parts.push(`Início: ${inicio}`);
  if (termino) parts.push(`Fim: ${termino}`);
  return parts.join(' • ');
};

const parseStoredDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const isInfoExtraActive = (item: InfoExtra, now = Date.now()) => {
  const begin = parseStoredDate(item.date_begin);
  const end = parseStoredDate(item.date_end);
  const meetsBegin = begin === null || now >= begin.getTime();
  const meetsEnd = end === null || now <= end.getTime();
  return meetsBegin && meetsEnd;
};

const StatusBadge = ({ active }: { active: boolean }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
      active
        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
        : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
    }`}
  >
    {active ? 'Ativo' : 'Inativo'}
  </span>
);

const InfoTip = ({ text }: { text: string }) => (
  <span className="group relative inline-flex items-center">
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-bold leading-none text-slate-500">
      i
    </span>
    <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 hidden w-max max-w-[280px] -translate-x-1/2 whitespace-normal rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium leading-5 text-white shadow-lg group-hover:block">
      {text}
    </span>
  </span>
);

const DeleteInfoExtraModal = ({
  target,
  saving,
  onCancel,
  onConfirm,
}: {
  target: InfoExtra | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!target) return null;

  const rangeLabel = getRangeLabel(target);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <AlertTriangle size={20} />
            </span>
            <div>
              <h3 className="text-base font-bold text-slate-900">Excluir informação?</h3>
              <p className="mt-1 text-sm leading-5 text-slate-600">
                Esta regra complementar deixará de ser considerada nas respostas do Atendabot.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Fechar modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <div className="max-h-28 overflow-y-auto break-words font-semibold text-slate-900 [overflow-wrap:anywhere]">
            {target.valor}
          </div>
          {rangeLabel && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
              <CalendarDays size={14} />
              <span>{rangeLabel}</span>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 text-sm font-bold text-white shadow-lg shadow-rose-600/20 transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Excluindo...' : 'Excluir informação'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoExtraPage = () => {
  const [items, setItems] = useState<InfoExtra[]>([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [newDateBegin, setNewDateBegin] = useState('');
  const [newDateEnd, setNewDateEnd] = useState('');
  const [newDateError, setNewDateError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingDateBegin, setEditingDateBegin] = useState('');
  const [editingDateEnd, setEditingDateEnd] = useState('');
  const [editingDateError, setEditingDateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InfoExtra | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return items;
    return items.filter((item) => {
      const active = isInfoExtraActive(item);
      return statusFilter === 'active' ? active : !active;
    });
  }, [items, statusFilter]);

  const emptyFilterMessage =
    statusFilter === 'active'
      ? 'Nenhuma informação ativa.'
      : 'Nenhuma informação inativa.';

  const buildPayload = (valor: string, begin: string, end: string) => {
    const payload: {
      valor: string;
      date_begin: string | null;
      date_end: string | null;
    } = {
      valor,
      date_begin: null,
      date_end: null,
    };

    const beginDate = parseDatetimeLocal(begin);
    const endDate = parseDatetimeLocal(end);

    if (beginDate) payload.date_begin = beginDate.toISOString();
    if (endDate) payload.date_end = endDate.toISOString();

    return payload;
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchInfoExtras();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) return;

    const dateError = validateDateRange(newDateBegin, newDateEnd);
    if (dateError) {
      setNewDateError(dateError);
      return;
    }

    setNewDateError(null);
    setSaving(true);
    try {
      const payload = buildPayload(trimmedValue, newDateBegin, newDateEnd);
      const created = await createInfoExtra(payload);
      setItems((prev) => [...prev, created]);
      setNewValue('');
      setNewDateBegin('');
      setNewDateEnd('');
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: number) => {
    const trimmedValue = editingValue.trim();
    if (!trimmedValue) return;

    const dateError = validateDateRange(editingDateBegin, editingDateEnd);
    if (dateError) {
      setEditingDateError(dateError);
      return;
    }

    setEditingDateError(null);
    setSaving(true);
    try {
      const payload = buildPayload(trimmedValue, editingDateBegin, editingDateEnd);
      await updateInfoExtra(id, payload);
      setItems((prev) => prev.map((item) => (
        item.id === id
          ? { ...item, valor: trimmedValue, date_begin: payload.date_begin, date_end: payload.date_end }
          : item
      )));
      setEditingId(null);
      setEditingValue('');
      setEditingDateBegin('');
      setEditingDateEnd('');
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;

    setSaving(true);
    try {
      await deleteInfoExtra(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <DeleteInfoExtraModal
        target={deleteTarget}
        saving={saving}
        onCancel={() => {
          if (!saving) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
      />

      {saving && !deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/55 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
            <span className="text-sm font-semibold text-slate-700">Salvando...</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8"
        >
          <div className="mx-auto w-full max-w-[1400px] space-y-5">
            <div className="mb-1 text-center">
              <h1 className="flex items-center justify-center gap-3 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                <CalendarDays size={28} className="text-[#2f7d72]" />
                Controle de regras adicionais
              </h1>
              <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Cadastre regras complementares que o Atendabot deve considerar nas respostas.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="grid gap-4 lg:grid-cols-[2fr_1fr_1fr_auto] lg:items-end">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700">Informação</label>
                  <input
                    type="text"
                    placeholder="Digite a informação extra"
                    className={inputClass}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span>Data de início (opcional)</span>
                    <InfoTip text={DATE_TOOLTIP_TEXT} />
                  </div>
                  <input
                    type="datetime-local"
                    placeholder="Data de início"
                    className={inputClass}
                    value={newDateBegin}
                    onChange={(e) => {
                      setNewDateBegin(e.target.value);
                      setNewDateError(null);
                    }}
                    step={60}
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span>Data de fim (opcional)</span>
                    <InfoTip text={DATE_TOOLTIP_TEXT} />
                  </div>
                  <input
                    type="datetime-local"
                    placeholder="Data de fim"
                    className={inputClass}
                    value={newDateEnd}
                    onChange={(e) => {
                      setNewDateEnd(e.target.value);
                      setNewDateError(null);
                    }}
                    min={getEndMinValue(newDateBegin)}
                    step={60}
                  />
                </div>

                <button
                  onClick={handleAdd}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2f7d72] px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#2f7d72]/25 transition hover:bg-[#27685f] disabled:opacity-60"
                >
                  <Plus size={16} />
                  Adicionar informação
                </button>
              </div>
              {newDateError && (
                <p className="mt-3 text-xs font-semibold text-rose-600">{newDateError}</p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-bold text-slate-900">Regras cadastradas</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                    {STATUS_FILTERS.map((option) => {
                      const selected = statusFilter === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setStatusFilter(option.value)}
                          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                            selected
                              ? 'bg-[#2f7d72] text-white shadow-sm'
                              : 'text-slate-600 hover:bg-white hover:text-slate-800'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                    {filteredItems.length === 1 ? '1 item' : `${filteredItems.length} itens`}
                  </span>
                </div>
              </div>

              {items.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">Nenhuma informação cadastrada.</p>
              ) : filteredItems.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">{emptyFilterMessage}</p>
              ) : (
                <div className="space-y-3">
                  {filteredItems.map((item) => {
                    const rangeLabel = getRangeLabel(item);
                    const active = isInfoExtraActive(item);
                    return (
                      <div
                        key={item.id}
                        className={`rounded-xl border p-4 ${
                          active
                            ? 'border-emerald-200 bg-emerald-50/40'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        {editingId === item.id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              className={inputClass}
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                            />

                            <div className="grid gap-3 md:grid-cols-2">
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                  <span>Data de início (opcional)</span>
                                  <InfoTip text={DATE_TOOLTIP_TEXT} />
                                </div>
                                <input
                                  type="datetime-local"
                                  placeholder="Data de início"
                                  className={inputClass}
                                  value={editingDateBegin}
                                  onChange={(e) => {
                                    setEditingDateBegin(e.target.value);
                                    setEditingDateError(null);
                                  }}
                                  step={60}
                                />
                              </div>

                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                  <span>Data de fim (opcional)</span>
                                  <InfoTip text={DATE_TOOLTIP_TEXT} />
                                </div>
                                <input
                                  type="datetime-local"
                                  placeholder="Data de fim"
                                  className={inputClass}
                                  value={editingDateEnd}
                                  onChange={(e) => {
                                    setEditingDateEnd(e.target.value);
                                    setEditingDateError(null);
                                  }}
                                  min={getEndMinValue(editingDateBegin)}
                                  step={60}
                                />
                              </div>
                            </div>

                            {editingDateError && (
                              <p className="text-xs font-semibold text-rose-600">{editingDateError}</p>
                            )}

                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                onClick={() => handleUpdate(item.id)}
                                disabled={saving}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-3 text-xs font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={() => {
                                  setEditingId(null);
                                  setEditingValue('');
                                  setEditingDateBegin('');
                                  setEditingDateEnd('');
                                  setEditingDateError(null);
                                }}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                            <div className="min-w-0 space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className={`min-w-0 break-words text-sm font-semibold [overflow-wrap:anywhere] ${
                                  active ? 'text-slate-800' : 'text-slate-500'
                                }`}>
                                  {item.valor}
                                </p>
                                <StatusBadge active={active} />
                              </div>
                              {rangeLabel && (
                                <p className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                                  <CalendarDays size={14} />
                                  <span>{rangeLabel}</span>
                                </p>
                              )}
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center gap-2 md:max-w-[220px] md:justify-end">
                              <button
                                onClick={() => {
                                  setEditingId(item.id);
                                  setEditingValue(item.valor);
                                  setEditingDateBegin(toDatetimeLocalInputValue(item.date_begin));
                                  setEditingDateEnd(toDatetimeLocalInputValue(item.date_end));
                                  setEditingDateError(null);
                                }}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                              >
                                <Pencil size={14} />
                                Editar
                              </button>
                              <button
                                onClick={() => setDeleteTarget(item)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                              >
                                <Trash2 size={14} />
                                Excluir
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </Layout>
  );
};

export default InfoExtraPage;
